import React from 'react';
import arevakhachIcon from 'assets/icons/arevakhach-icon.svg';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { THEME } from './theme';

// The map's header row (mock 18a): the mark, the label, the in/out legend and
// In the mail now on the left; the owner's avatar and name on the right with
// the same Elite ring and badge they carry everywhere else (round 1, items 2,
// 4 and 5). The share PNG draws its own header on the canvas (drawCard.ts).

export interface TradeMapOwner {
  username: string;
  avatarUrl: string | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

/** "My trade map" for the owner; "{username}'s trade map" for anyone else, signed in or out */
export const tradeMapLabel = (ownerUsername: string, isOwnProfile: boolean): string =>
  isOwnProfile ? 'My trade map' : `${ownerUsername}'s trade map`;

interface Props {
  owner: TradeMapOwner;
  isOwnProfile: boolean;
  countries: number;
  inTransit: number;
  /** Phones: mark, label and identity only; the legend is the compact one at the bottom */
  compact?: boolean;
}

const Bow: React.FC<{ color: string; up: boolean }> = ({ color, up }) => (
  <svg width="16" height="9" viewBox="0 0 16 9" aria-hidden>
    <path d={up ? 'M1 7.5 Q8 -2.5 15 7.5' : 'M1 1.5 Q8 11.5 15 1.5'} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const TradeMapHeader: React.FC<Props> = ({ owner, isOwnProfile, countries, inTransit, compact = false }) => (
  <div className={`ptm-header${compact ? ' ptm-header--compact' : ''}`} data-testid="ptm-header">
    <div className="ptm-header-left">
      <img src={arevakhachIcon} alt="" width={16} height={16} className="ptm-header-mark" />
      <span className="ptm-header-label" data-testid="ptm-header-label">
        {tradeMapLabel(owner.username, isOwnProfile)} · {countries.toLocaleString('en-US')} {plural(countries, 'country', 'countries')}
      </span>
      {!compact && (
        <span className="ptm-header-legend" data-testid="ptm-legend">
          <span className="ptm-legend-item">
            <Bow color={THEME.inbound} up />
            in
          </span>
          <span className="ptm-legend-item">
            <Bow color={THEME.outbound} up={false} />
            out
          </span>
          {inTransit > 0 && (
            <span className="ptm-legend-item ptm-legend-item--live" data-testid="ptm-legend-live">
              <svg width="16" height="9" viewBox="0 0 16 9" aria-hidden>
                <path d="M1 4.5 H15" fill="none" stroke={THEME.inbound} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 2.5" className="ptm-legend-march" />
              </svg>
              In the mail now · {inTransit.toLocaleString('en-US')}
            </span>
          )}
        </span>
      )}
    </div>
    <div className="ptm-header-identity" data-testid="ptm-header-identity">
      <AvatarWithFlag
        avatarUrl={owner.avatarUrl}
        username={owner.username}
        size={compact ? 20 : 24}
        isElite={owner.isElite}
        showAnimatedBorder={owner.showAnimatedBorder}
        borderColor1={owner.borderColor1}
        borderColor2={owner.borderColor2}
      />
      <span className="ptm-header-name">{owner.username}</span>
      <EliteBadge isElite={!!owner.isElite} size={compact ? 12 : 14} />
    </div>
  </div>
);

export default TradeMapHeader;
